import { useState, useEffect, useRef } from 'react';
import { supabase } from '../lib/supabase';
import { useAgency } from '../lib/AgencyContext';
import { useI18n } from '../lib/i18n';
import { ACTIVITY_CODES } from '../lib/helpers';
import Icon from './Icon';
import { fileToLogoDataUrl } from './logoUtil';
import OwnAccounts from './OwnAccounts';
import { lookupCompany, splitAddress, ownerFromName, foreignFields, suggestAccounts } from '../lib/banks';
import { digitsOnly, pibError } from '../lib/validation';

// Polja koja se mogu preuzeti po PIB-u (prikazuju se pre primene)
const REG_LABELS = {
  company_name: 'Pun naziv firme', owner_name: 'Ime i prezime obveznika', mb: 'Matični broj',
  street: 'Ulica', street_number: 'Broj', city: 'Mesto', activity_code: 'Šifra delatnosti',
  account_dom: 'Tekući račun', account_ino: 'Račun za inostranstvo (IBAN)', swift: 'SWIFT', iban_bank: 'Banka',
};

const FIELDS = [
  'company_name', 'short_name', 'name_display', 'pib', 'mb', 'registration_date', 'activity_code', 'activity_name',
  'city', 'postal_code', 'street', 'street_number', 'phone', 'email', 'account_dom', 'account_ino', 'iban_bank', 'swift',
  'owner_name', 'taxpayer_code', 'logo_data',
];

export default function AgencySettings() {
  const { activeAgencyId, profile, refreshProfile, isAdmin } = useAgency();
  const { t, tr } = useI18n();
  const L = tr;
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [saveError, setSaveError] = useState('');
  const [logoError, setLogoError] = useState('');
  const fileRef = useRef(null);

  const [form, setForm] = useState(() => {
    const f = {};
    FIELDS.forEach((k) => { f[k] = ''; });
    f.name_display = 'full';
    return f;
  });

  useEffect(() => {
    if (!profile) return;
    setForm((f) => {
      const next = { ...f };
      FIELDS.forEach((k) => { if (profile[k] !== null && profile[k] !== undefined) next[k] = profile[k]; });
      if (!next.name_display) next.name_display = 'full';
      return next;
    });
  }, [profile]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  // Preuzimanje po PIB-u: ništa se ne menja dok korisnik ne klikne "Primeni" (pa "Sačuvaj izmene")
  const [reg, setReg] = useState({ busy: false, err: '', changes: null, accounts: [] });
  const fetchRegistry = async () => {
    setReg({ busy: true, err: '', changes: null, accounts: [] });
    try {
      const { company: c, accounts } = await lookupCompany(digitsOnly(form.pib));
      const { street, street_number: streetNumber } = splitAddress(c.address);
      const { dom, fx } = suggestAccounts(accounts);
      const next = {
        company_name: c.name, owner_name: form.owner_name ? '' : ownerFromName(c.name), mb: digitsOnly(c.mb),
        street: c.address ? street : '', street_number: c.address ? streetNumber : '', city: c.city,
        activity_code: c.activity_code,
        account_dom: form.account_dom ? '' : dom?.account,
        ...(!form.account_ino && fx ? foreignFields(fx) : {}),
      };
      const changes = Object.entries(next)
        .filter(([k, v]) => v && String(v).trim() !== String(form[k] || '').trim())
        .map(([k, v]) => ({ key: k, from: form[k] || '', to: v, ...(k === 'activity_code' ? { name: c.activity_name || '' } : {}) }));
      setReg({ busy: false, err: '', changes, accounts });
    } catch (err) {
      setReg({ busy: false, err: err.message, changes: null, accounts: [] });
    }
  };
  const applyRegistry = () => {
    setForm((f) => {
      const next = { ...f };
      reg.changes.forEach((ch) => {
        next[ch.key] = ch.to;
        if (ch.key === 'activity_code') next.activity_name = ch.name || '';
      });
      return next;
    });
    setReg((r) => ({ ...r, changes: [] }));
  };

  // Administrator: šifarnici statusa i vrsta računa iz registra (za proveru)
  const [codes, setCodes] = useState('');
  const loadCodes = async () => {
    setCodes('…');
    const { data, error } = await supabase.functions.invoke('nbs-company-lookup', { body: { action: 'codes' } });
    setCodes(error ? String(error.message) : JSON.stringify(data, null, 2));
  };
  // Administrator: koje pretrage registra vraćaju devizne račune (po PIB-u, MB-u, broju računa)
  const probe = async () => {
    const account = window.prompt('Broj deviznog računa za proveru (može prazno):', '') || '';
    setCodes('…');
    const { data, error } = await supabase.functions.invoke('nbs-company-lookup', {
      body: { action: 'probe', pib: digitsOnly(form.pib), mb: digitsOnly(form.mb), account },
    });
    setCodes(error ? String(error.message) : JSON.stringify(data, null, 2));
  };

  const pickLogo = async (file) => {
    setLogoError('');
    try {
      const dataUrl = await fileToLogoDataUrl(file);
      setForm((f) => ({ ...f, logo_data: dataUrl }));
    } catch (err) {
      setLogoError(err.message);
    }
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    setSaved(false);
    setSaveError('');
    // Isti PIB ne sme da ima druga firma na ovom nalogu
    const pib = digitsOnly(form.pib);
    if (pib) {
      const { data: others } = await supabase.from('agency_profile').select('agency_id, pib, company_name').neq('agency_id', activeAgencyId);
      const dup = (others || []).find((p) => digitsOnly(p.pib) === pib);
      if (dup) {
        setSaving(false);
        setSaveError(L(`Ovaj PIB već ima druga firma na vašem nalogu (${dup.company_name || 'bez naziva'}).`, `Another company on your account already has this tax ID (${dup.company_name || 'no name'}).`));
        return;
      }
    }
    const payload = { agency_id: activeAgencyId, onboarding_complete: true };
    FIELDS.forEach((k) => { payload[k] = form[k] ?? ''; });
    payload.registration_date = form.registration_date || null;
    payload.logo_data = form.logo_data || null;
    payload.short_name = (form.short_name || '').trim() || null;
    payload.name_display = form.short_name ? (form.name_display || 'full') : 'full';
    const { error } = await supabase.from('agency_profile').upsert(payload, { onConflict: 'agency_id' });
    setSaving(false);
    if (error) {
      setSaveError(/agency_profile_unique_pib/.test(error.message || '')
        ? L('Ovaj PIB već ima druga firma na vašem nalogu.', 'Another company on your account already has this tax ID.')
        : error.message);
      return;
    }
    refreshProfile();
    setSaved(true);
  };

  return (
    <div style={styles.page}>
      <h1 style={styles.heading}>{t('agency')}</h1>

      <form onSubmit={handleSave} style={styles.card}>
        {/* Logo */}
        <p style={{ ...styles.sectionLabel, marginTop: 0 }}>{L('Logo na fakturi', 'Invoice logo')}</p>
        <div style={styles.logoRow}>
          <div style={styles.logoPreview}>
            {form.logo_data
              ? <img src={form.logo_data} alt="Logo" style={styles.logoImg} />
              : <span style={styles.logoEmpty}>{L('Nema loga', 'No logo')}</span>}
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            <input
              ref={fileRef}
              type="file"
              accept="image/png,image/jpeg,image/webp,image/svg+xml"
              style={{ display: 'none' }}
              onChange={(e) => { pickLogo(e.target.files?.[0]); e.target.value = ''; }}
            />
            <button type="button" onClick={() => fileRef.current?.click()} style={styles.outlineBtn}>
              <Icon name="download" size={16} style={{ transform: 'rotate(180deg)' }} />
              {form.logo_data ? L('Promeni logo', 'Change logo') : L('Dodaj logo', 'Add logo')}
            </button>
            {form.logo_data && (
              <button type="button" onClick={() => setForm((f) => ({ ...f, logo_data: '' }))} style={styles.linkDanger}>
                {L('Ukloni logo', 'Remove logo')}
              </button>
            )}
            <span style={styles.hint}>{L('PNG, JPG ili SVG. Slika se sama smanji.', 'PNG, JPG or SVG. Resized automatically.')}</span>
          </div>
        </div>
        {logoError && <div style={styles.errorBox}>{logoError}</div>}

        <p style={styles.sectionLabel}>{L('Podaci o firmi', 'Company details')}</p>
        <label style={styles.label}>{t('pibLabel')}</label>
        <input value={form.pib} onChange={set('pib')} style={styles.input} />
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', margin: '-4px 0 12px' }}>
          <button
            type="button"
            onClick={fetchRegistry}
            disabled={reg.busy || digitsOnly(form.pib).length !== 9 || Boolean(pibError(digitsOnly(form.pib)))}
            style={{ ...styles.outlineBtn, ...(reg.busy || digitsOnly(form.pib).length !== 9 ? { opacity: 0.6 } : {}) }}
          >
            <Icon name="download" size={15} />
            {reg.busy ? L('Preuzimam…', 'Fetching…') : L('Preuzmi podatke po PIB-u', 'Fetch details by tax ID')}
          </button>
          {isAdmin && (
            <>
              <button type="button" onClick={loadCodes} style={styles.outlineBtn}>{L('Šifarnici (admin)', 'Code lists (admin)')}</button>
              <button type="button" onClick={probe} style={styles.outlineBtn}>{L('Dijagnostika računa (admin)', 'Account diagnostics (admin)')}</button>
            </>
          )}
        </div>
        {reg.err && <div style={styles.errorBox}>{reg.err}</div>}
        {reg.changes && reg.changes.length === 0 && !reg.err && (
          <div style={styles.successBox}>{L('Podaci se slažu sa registrom.', 'Details match the registry.')}</div>
        )}
        {reg.changes && reg.changes.length > 0 && (
          <div style={styles.choiceBox}>
            <div style={styles.choiceTitle}>{L('Pronađene izmene:', 'Changes found:')}</div>
            {reg.changes.map((ch) => (
              <div key={ch.key} style={{ fontSize: 13, marginBottom: 6 }}>
                <span style={{ fontWeight: 600 }}>{REG_LABELS[ch.key]}:</span>{' '}
                {ch.from && <span style={{ color: 'var(--ink-500)', textDecoration: 'line-through' }}>{ch.from}</span>}
                {ch.from && ' → '}
                <span style={{ color: 'var(--ink-900)' }}>{ch.to}{ch.name ? ` — ${ch.name}` : ''}</span>
              </div>
            ))}
            <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
              <button type="button" onClick={applyRegistry} style={styles.saveBtn}>{L('Primeni', 'Apply')}</button>
              <button type="button" onClick={() => setReg((r) => ({ ...r, changes: null }))} style={styles.outlineBtn}>{L('Odustani', 'Cancel')}</button>
            </div>
            <div style={{ ...styles.hint, marginTop: 8 }}>{L('Posle primene kliknite „Sačuvaj izmene“ na dnu.', 'After applying, click “Save changes” at the bottom.')}</div>
          </div>
        )}
        {isAdmin && codes && <pre style={styles.codes}>{codes}</pre>}

        <label style={styles.label}>{L('Pun naziv firme *', 'Full company name *')}</label>
        <input required value={form.company_name} onChange={set('company_name')} style={styles.input} />

        <label style={styles.label}>{L('Skraćeni naziv firme', 'Short company name')}</label>
        <input value={form.short_name || ''} onChange={set('short_name')} placeholder={L('npr. FinArt Studio', 'e.g. FinArt Studio')} style={styles.input} />

        {form.short_name && (
          <div style={styles.choiceBox}>
            <div style={styles.choiceTitle}>{L('Na fakturi podrazumevano štampaj:', 'Print on invoices by default:')}</div>
            <div style={styles.choiceRow}>
              {[['full', L('Pun naziv', 'Full name'), form.company_name], ['short', L('Skraćeni naziv', 'Short name'), form.short_name]].map(([v, label, example]) => (
                <label key={v} style={{ ...styles.choice, ...(form.name_display === v ? styles.choiceOn : {}) }}>
                  <input type="radio" name="name_display" checked={form.name_display === v} onChange={() => setForm((f) => ({ ...f, name_display: v }))} />
                  <span>
                    <span style={styles.choiceLabel}>{label}</span>
                    <span style={styles.choiceExample}>{example || '—'}</span>
                  </span>
                </label>
              ))}
            </div>
            <div style={styles.hint}>{L('Izbor možeš promeniti i na svakoj fakturi posebno.', 'You can also change it on each invoice.')}</div>
          </div>
        )}

        <label style={styles.label}>{L('Ime i prezime obveznika', 'Owner full name')}</label>
        <input value={form.owner_name || ''} onChange={set('owner_name')} style={styles.input} />

        <div style={styles.grid2}>
          <div>
            <label style={styles.label}>{t('mbLabel')}</label>
            <input value={form.mb} onChange={set('mb')} style={styles.input} />
          </div>
          <div>
            <label style={styles.label}>{t('regDateLabel')}</label>
            <input type="date" value={form.registration_date || ''} onChange={set('registration_date')} style={styles.input} />
          </div>
        </div>

        <label style={styles.label}>{t('activityCodeLabel')}</label>
        <select value={form.activity_code || ''} onChange={(e) => {
          const found = ACTIVITY_CODES.find((a) => a.code === e.target.value);
          setForm((f) => ({ ...f, activity_code: e.target.value, activity_name: found?.name || '' }));
        }} style={styles.input}>
          <option value="">{t('chooseActivityCode')}</option>
          {form.activity_code && !ACTIVITY_CODES.some((a) => a.code === form.activity_code) && (
            <option value={form.activity_code}>{form.activity_code} — {form.activity_name || 'ranije izabrana šifra'}</option>
          )}
          {ACTIVITY_CODES.map((a) => <option key={a.code} value={a.code}>{a.code} — {a.name}</option>)}
        </select>

        <div style={styles.grid2}>
          <div>
            <label style={styles.label}>{t('cityLabel')}</label>
            <input value={form.city} onChange={set('city')} style={styles.input} />
          </div>
          <div>
            <label style={styles.label}>{t('postalCodeLabel')}</label>
            <input value={form.postal_code} onChange={set('postal_code')} style={styles.input} />
          </div>
        </div>

        <div style={styles.grid2}>
          <div>
            <label style={styles.label}>{t('streetLabel')}</label>
            <input value={form.street} onChange={set('street')} style={styles.input} />
          </div>
          <div>
            <label style={styles.label}>{t('streetNumberLabel')}</label>
            <input value={form.street_number} onChange={set('street_number')} style={styles.input} />
          </div>
        </div>

        <div style={styles.grid2}>
          <div>
            <label style={styles.label}>{t('emailLabel')}</label>
            <input type="email" value={form.email} onChange={set('email')} style={styles.input} />
          </div>
          <div>
            <label style={styles.label}>{t('phoneLabel')}</label>
            <input value={form.phone} onChange={set('phone')} style={styles.input} />
          </div>
        </div>

        <p style={styles.sectionLabel}>{t('accountsSection')}</p>
        <label style={styles.label}>{t('domesticAccountLabel')}</label>
        <input value={form.account_dom} onChange={set('account_dom')} placeholder="000-0000000000000-00" style={styles.input} />
        <div style={styles.grid2}>
          <div>
            <label style={styles.label}>{t('foreignIbanLabel')}</label>
            <input value={form.account_ino} onChange={set('account_ino')} style={styles.input} />
            {reg.accounts.length > 0 && !form.account_ino && (
              <div style={{ ...styles.hint, margin: '-8px 0 12px' }}>
                {L('Upišite IBAN iz instrukcija vaše banke za priliv iz inostranstva.', 'Enter the IBAN from your bank’s instructions for incoming payments from abroad.')}
              </div>
            )}
          </div>
          <div>
            <label style={styles.label}>{t('swiftLabel')}</label>
            <input value={form.swift} onChange={set('swift')} style={styles.input} />
          </div>
        </div>
        <label style={styles.label}>{t('bankLabel')}</label>
        <input value={form.iban_bank} onChange={set('iban_bank')} style={styles.input} />
        <OwnAccounts
          accounts={reg.accounts}
          accountDom={form.account_dom}
          accountIno={form.account_ino}
          onPickDom={(a) => setForm((f) => ({ ...f, account_dom: a.account }))}
          onPickFx={(a, fx) => setForm((f) => ({ ...f, ...fx }))}
        />

        {saveError && <div style={styles.errorBox}>{saveError}</div>}
        {saved && <div style={styles.successBox}>{t('savedNote')}</div>}
        <button type="submit" disabled={saving} style={styles.saveBtn}>{saving ? t('savingEllipsisShort') : t('saveChanges')}</button>
      </form>
    </div>
  );
}

const styles = {
  page: { maxWidth: 600, margin: '0 auto' },
  heading: { fontSize: 22, marginBottom: 16 },
  card: { background: 'var(--surface)', borderRadius: 10, padding: 22, border: '1px solid var(--line)', display: 'flex', flexDirection: 'column' },
  label: { fontSize: 13, fontWeight: 600, color: 'var(--ink-700)', marginBottom: 5, display: 'block' },
  input: { border: '1.5px solid var(--line)', borderRadius: 10, padding: '11px 13px', fontSize: 14, width: '100%', marginBottom: 12 },
  grid2: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 },
  sectionLabel: { fontSize: 12, fontWeight: 700, color: 'var(--ink-500)', textTransform: 'uppercase', letterSpacing: '0.06em', margin: '14px 0 10px' },
  logoRow: { display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap', marginBottom: 6 },
  logoPreview: { width: 200, height: 90, border: '1.5px dashed var(--line)', borderRadius: 8, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'var(--paper)', overflow: 'hidden' },
  logoImg: { maxWidth: '90%', maxHeight: '80%', objectFit: 'contain' },
  logoEmpty: { fontSize: 12, color: 'var(--ink-300)' },
  outlineBtn: { display: 'flex', alignItems: 'center', gap: 8, minHeight: 34, border: '1px solid var(--line)', background: 'var(--surface)', color: 'var(--teal-800)', borderRadius: 10, padding: '0 14px', fontWeight: 600, fontSize: 13 },
  linkDanger: { border: 'none', background: 'none', color: 'var(--red-600)', fontWeight: 600, fontSize: 13, padding: 0, textAlign: 'left' },
  hint: { fontSize: 12, color: 'var(--ink-500)' },
  choiceBox: { background: 'var(--paper)', border: '1px solid var(--line)', borderRadius: 8, padding: 14, marginBottom: 14 },
  choiceTitle: { fontSize: 13, fontWeight: 700, color: 'var(--ink-900)', marginBottom: 10 },
  choiceRow: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 8, marginBottom: 8 },
  choice: { display: 'flex', alignItems: 'flex-start', gap: 10, background: 'var(--surface)', border: '1.5px solid var(--line)', borderRadius: 10, padding: '10px 12px', cursor: 'pointer' },
  choiceOn: { borderColor: 'var(--lavender-700)', background: 'var(--lavender-100)' },
  choiceLabel: { display: 'block', fontSize: 13, fontWeight: 700, color: 'var(--ink-900)' },
  choiceExample: { display: 'block', fontSize: 12, color: 'var(--ink-500)', marginTop: 2 },
  errorBox: { background: 'var(--red-100)', color: 'var(--red-600)', borderRadius: 10, padding: '8px 12px', fontSize: 12, marginBottom: 12, wordBreak: 'break-word' },
  successBox: { background: 'var(--forest-100)', color: 'var(--forest-600)', borderRadius: 10, padding: '8px 12px', fontSize: 12, marginBottom: 12 },
  codes: { fontSize: 11, background: 'var(--paper)', border: '1px solid var(--line)', borderRadius: 8, padding: 10, maxHeight: 260, overflow: 'auto', whiteSpace: 'pre-wrap', marginBottom: 12 },
  saveBtn: { marginTop: 12, minHeight: 34, background: 'var(--teal-800)', color: '#fff', border: 'none', borderRadius: 8, padding: '0 14px', fontWeight: 700, fontSize: 13 },
};
